import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { IoChevronDown, IoClose, IoContract, IoPlayCircle, IoExpand, IoPause, IoPlay, IoSync, IoVolumeHigh, IoVolumeMute, IoPersonCircle } from 'react-icons/io5';
import { loadYouTubeApi } from '../utils/youtubeIframeApi';
import { useLanguage } from '../i18n/LanguageContext';
import { useSocket } from '../socket/SocketContext';
import { VideoTile } from './VideoGridOverlay';
import { useOverlayFullscreen } from '../hooks/useOverlayFullscreen';
import { useLayoutMode } from '../hooks/useLayoutMode';
import { useAutoHideChrome } from '../hooks/useAutoHideChrome';

// No periodic server broadcast (see videoWatchSession.js) - the server only
// pushes a new state on open/play/pause/seek, so staying in sync the rest
// of the time is entirely this component's job: reconcile against the
// server-derived "expected second" on this cadence, correcting only past a
// threshold so small buffering jitter doesn't cause constant micro-seeks.
const DRIFT_CHECK_MS = 2000;
const DRIFT_THRESHOLD_SEC = 1.5;

// Buffering barrier tuning (see VideoWatchSession for the server half).
const STALL_DEBOUNCE_MS = 1200; // how long a stall must persist before it pauses the room
const READY_POLL_MS = 250; // how often to re-check whether this client has caught up

// One personal preference shared across every watch-together session and
// video (not per-video) - same "it's about this person's ears, not this
// particular clip" reasoning as useVoiceConnection.js's participant volumes,
// just simpler here since there's no per-identity boosting need (a video's
// own native audio never needs to go above 100%, unlike a quiet remote mic).
const VOLUME_STORAGE_KEY = 'videoWatchVolume';

function loadStoredVolume() {
  const stored = parseFloat(localStorage.getItem(VOLUME_STORAGE_KEY));
  return Number.isFinite(stored) ? Math.min(1, Math.max(0, stored)) : 1;
}

function formatTime(seconds) {
  if (!seconds && seconds !== 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Both surfaces below expose the exact same imperative shape
// (seekTo/play/pause/getCurrentSec/getDurationSec) so the overlay's
// sync/drift/control logic never has to know or care which source type
// it's driving - same "kim sahip" split as VideoTile's track.attach/detach
// in VideoGridOverlay.js: the surface owns the underlying player, the
// overlay owns when to call into it.

const YouTubeSurface = forwardRef(function YouTubeSurface({ videoId, onReady, onStallChange, onEnded }, ref) {
  const wrapperRef = useRef(null);
  const playerRef = useRef(null);
  // Read through a ref so the player is only ever constructed once per
  // videoId - baking the callback into the effect's deps would tear down and
  // rebuild the whole <iframe> every time the parent re-renders with a new
  // closure.
  const onStallChangeRef = useRef(onStallChange);
  onStallChangeRef.current = onStallChange;
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;

  useEffect(() => {
    let cancelled = false;
    let player = null;
    // The YouTube IFrame API replaces whatever element it's given with its
    // own <iframe> (removes the original node from the DOM entirely) -
    // handing it wrapperRef's own div directly would leave React holding a
    // stale reference to a node that's no longer actually there, which
    // throws a removeChild NotFoundError the next time this component
    // unmounts (e.g. switching to a different video via the `key` below).
    // A plain, manually-created child keeps that swap entirely outside
    // React's reconciliation - wrapperRef itself is never touched by the
    // YouTube API and always stays exactly where React put it.
    const target = document.createElement('div');
    wrapperRef.current.appendChild(target);

    loadYouTubeApi().then((YT) => {
      if (cancelled) return;
      // controls:0 - our own control bar (play/pause + seek slider) is the
      // only one anyone interacts with, so a direct-URL video and a YouTube
      // one genuinely offer the same controls, not YouTube's chrome plus a
      // second, redundant one layered under it.
      player = new YT.Player(target, {
        videoId,
        width: '100%',
        height: '100%',
        // cc_load_policy:0 - without it YouTube decides on its own (based on
        // the viewer's YouTube account settings) whether to force captions
        // on for this embed, which we don't want since this overlay has no
        // caption toggle of its own to turn them back off.
        playerVars: { autoplay: 0, controls: 0, modestbranding: 1, rel: 0, playsinline: 1, cc_load_policy: 0 },
        events: {
          onReady: () => {
            if (cancelled) return;
            playerRef.current = player;
            onReady();
          },
          // YT.PlayerState.BUFFERING (3) is this surface's equivalent of a
          // native <video>'s waiting/playing pair - reported up so the
          // overlay can pause the whole room on this participant.
          onStateChange: (e) => {
            if (cancelled) return;
            if (e.data === YT.PlayerState.BUFFERING) onStallChangeRef.current(true);
            else if (e.data === YT.PlayerState.PLAYING) onStallChangeRef.current(false);
            else if (e.data === YT.PlayerState.ENDED) onEndedRef.current();
          }
        }
      });
    });
    return () => {
      cancelled = true;
      playerRef.current = null;
      if (player && player.destroy) player.destroy();
      else if (target.parentNode) target.parentNode.removeChild(target);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  useImperativeHandle(ref, () => ({
    seekTo: (sec) => { if (playerRef.current) playerRef.current.seekTo(sec, true); },
    play: () => { if (playerRef.current) playerRef.current.playVideo(); },
    pause: () => { if (playerRef.current) playerRef.current.pauseVideo(); },
    getCurrentSec: () => (playerRef.current ? playerRef.current.getCurrentTime() : 0),
    getDurationSec: () => (playerRef.current ? playerRef.current.getDuration() : 0),
    // PLAYING or BUFFERING - i.e. the player accepted a play() and is on
    // its way. Anything else after a play() means it was refused (mobile
    // autoplay rules), see autoplayBlocked in the overlay.
    isPlaying: () => {
      const state = playerRef.current ? playerRef.current.getPlayerState() : -1;
      return state === 1 || state === 3;
    },
    // vol is 0-1 (this overlay's own convention, matching a native <video>
    // element) - the YouTube API's own scale is 0-100, converted here so
    // the overlay's volume logic doesn't need to know which surface it's
    // driving, same as every other imperative method on this interface.
    setVolume: (vol) => { if (playerRef.current) playerRef.current.setVolume(Math.round(vol * 100)); }
  }), []);

  return <div className="VideoWatchSurface" ref={wrapperRef} />;
});

const DirectUrlSurface = forwardRef(function DirectUrlSurface({ url, onReady, onStallChange, onEnded }, ref) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const onStallChangeRef = useRef(onStallChange); // same rationale as YouTubeSurface's
  onStallChangeRef.current = onStallChange;
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;
    let cancelled = false;

    // Progressive files (mp4/webm/ogg) play through the browser's native
    // <video> with zero extra work. .m3u8 (HLS) links need hls.js -
    // Electron's Chromium, unlike Safari, has no native HLS support -
    // loaded lazily (dynamic import) so plain files never pay for it.
    if (/\.m3u8(\?|$)/i.test(url)) {
      import('hls.js').then(({ default: Hls }) => {
        if (cancelled) return;
        if (Hls.isSupported()) {
          const hls = new Hls();
          hls.loadSource(url);
          hls.attachMedia(el);
          hlsRef.current = hls;
        } else {
          el.src = url; // native HLS support (e.g. Safari) - harmless fallback
        }
      });
    } else {
      el.src = url;
    }

    const handleLoadedMetadata = () => { if (!cancelled) onReady(); };
    // 'waiting' fires when playback stops for lack of buffered data,
    // 'stalled' when the network itself goes quiet - both mean this
    // participant has fallen behind. 'playing'/'canplay' are the recovery
    // signals; the overlay debounces the whole thing (see STALL_*_MS) so a
    // brief hiccup never pauses the room.
    const handleStall = () => { if (!cancelled) onStallChangeRef.current(true); };
    const handleResume = () => { if (!cancelled) onStallChangeRef.current(false); };
    // Natural end-of-playback - not a stall, so it must NOT go through
    // onStallChange (nothing will ever un-stall it, since the element just
    // sits paused at its own duration forever).
    const handleEnded = () => { if (!cancelled) onEndedRef.current(); };
    el.addEventListener('loadedmetadata', handleLoadedMetadata);
    el.addEventListener('waiting', handleStall);
    el.addEventListener('stalled', handleStall);
    el.addEventListener('playing', handleResume);
    el.addEventListener('canplay', handleResume);
    el.addEventListener('ended', handleEnded);
    return () => {
      cancelled = true;
      el.removeEventListener('loadedmetadata', handleLoadedMetadata);
      el.removeEventListener('waiting', handleStall);
      el.removeEventListener('stalled', handleStall);
      el.removeEventListener('playing', handleResume);
      el.removeEventListener('canplay', handleResume);
      el.removeEventListener('ended', handleEnded);
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useImperativeHandle(ref, () => ({
    seekTo: (sec) => { if (videoRef.current) videoRef.current.currentTime = sec; },
    play: () => { if (videoRef.current) videoRef.current.play().catch(() => {}); },
    pause: () => { if (videoRef.current) videoRef.current.pause(); },
    getCurrentSec: () => (videoRef.current ? videoRef.current.currentTime : 0),
    getDurationSec: () => (videoRef.current && Number.isFinite(videoRef.current.duration) ? videoRef.current.duration : 0),
    isPlaying: () => !!(videoRef.current && !videoRef.current.paused),
    setVolume: (vol) => { if (videoRef.current) videoRef.current.volume = vol; }
  }), []);

  // No `muted` attribute, unlike VideoTile's camera preview in
  // VideoGridOverlay.js - this element IS the audio source (see the "watch
  // party" decision: each participant hears their own local playback,
  // there's no separate LiveKit audio path to double up against here).
  return <video ref={videoRef} className="VideoWatchSurface VideoWatchSurfaceNative" playsInline />;
});

// Rendered as a sibling of .VoicePanel (same reasoning as MusicSearchModal/
// VideoGridOverlay) - only mounted while a watch-together session is
// actually open (status !== 'idle'), see VoiceRoomPanel.js.
export default function VideoWatchOverlay({
  roomLabel, videoWatchState, onPlay, onPause, onSeek, onClose, onBuffering, onReady: onReportReady,
  participants, videoTracks, occupants, minimized = false, onMinimize
}) {
  const { t } = useLanguage();
  const { pushToast } = useSocket();
  const { status, video, positionSec, updatedAt, waitingOn, bufferingReason } = videoWatchState;
  const buffering = status === 'buffering';

  const overlayRef = useRef(null);
  const surfaceRef = useRef(null);
  const stallTimerRef = useRef(null);
  // Lets the debounced stall handler read the *current* status when it
  // fires, rather than whatever it was when the timer was scheduled.
  const statusRef = useRef(status);
  statusRef.current = status;
  const [ready, setReady] = useState(false);
  const { isFullscreen, pseudoFullscreen, toggleFullscreen, leaveFullscreen } = useOverlayFullscreen(overlayRef);
  const { isMobile } = useLayoutMode();
  // Phone + fullscreen: the picture takes the whole screen edge to edge,
  // header and controls float over it and auto-hide (mobile.css).
  const immersive = isMobile && isFullscreen;
  const { chromeHidden, poke, toggle: toggleChrome } = useAutoHideChrome(immersive, status === 'playing');
  const [localDurationSec, setLocalDurationSec] = useState(0);
  const [displayedSec, setDisplayedSec] = useState(positionSec);
  const [dragging, setDragging] = useState(false);
  const [dragSec, setDragSec] = useState(0);
  const [volume, setVolume] = useState(loadStoredVolume);

  // `updatedAt` is an epoch stamped on the SERVER's clock (see
  // VideoWatchSession's _emitChange) - subtracting it from THIS client's own
  // Date.now() only gives the right answer if the two clocks agree, which in
  // practice they often don't (an unsynced OS clock a few seconds off is
  // common, and every participant drifts by a different amount). That was
  // producing the reported 10+ second gaps between viewers. Anchoring
  // "elapsed" to the moment THIS client actually received the update instead
  // - keeping the server's positionSec as the anchor position - cancels any
  // clock skew out entirely: both Date.now() reads below come from the same
  // clock, so a constant offset (however large) affects them equally and
  // disappears in the subtraction. Only recomputed when the server pushes a
  // genuinely new state (updatedAt changing), not on every render. Kept as
  // a dependency even though the memo body never reads it directly - it's
  // the actual "a new event happened" signal (e.g. pausing then resuming at
  // the exact same positionSec still needs a fresh anchor, or expectedSec()
  // would keep computing elapsed time from whenever the OLD anchor was set).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const localAnchor = useMemo(() => ({ at: Date.now(), positionSec }), [updatedAt, positionSec]);

  const expectedSec = useCallback(
    () => (status === 'playing' ? localAnchor.positionSec + (Date.now() - localAnchor.at) / 1000 : positionSec),
    [status, positionSec, localAnchor]
  );

  // Both header buttons leave fullscreen first: a minimized overlay can't
  // stay fullscreen, and onClose opens a confirm dialog that would render
  // behind the fullscreen element, invisible.
  const handleMinimize = () => { leaveFullscreen(); if (onMinimize) onMinimize(); };
  const handleStop = () => { leaveFullscreen(); onClose(); };

  // A different video (not just a play/pause/seek on the SAME one) swaps
  // the surface entirely (key below) - `ready` has to reset with it, or the
  // reconciliation effect right after would try to drive a surface that's
  // mid-remount for the new source.
  useEffect(() => {
    setReady(false);
    setLocalDurationSec(0);
  }, [video.videoId, video.url]);

  // Reconciles the surface against the server's state on every broadcast
  // (open/play/pause/seek - see videoWatchSession.js, there's no periodic
  // one) - NOT in response to the surface's own playback, which is exactly
  // what would create an echo loop back to the server (see onPlay/onPause/
  // onSeek below, which only ever fire from direct user interaction with
  // this component's own controls).
  useEffect(() => {
    if (!ready || !surfaceRef.current) return;
    surfaceRef.current.seekTo(expectedSec());
    if (status === 'playing') surfaceRef.current.play(); else surfaceRef.current.pause();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, status, positionSec, updatedAt]);

  // Buffering barrier, client half (server half: VideoWatchSession). While
  // the room is waiting, everyone sits paused at the frozen position; this
  // client seeks there, buffers, and acks once its own player reports it
  // can actually play from that point. Only then does the server flip
  // everyone back to 'playing' at once.
  useEffect(() => {
    if (!buffering || !ready || !surfaceRef.current) return undefined;
    const surface = surfaceRef.current;
    surface.pause();
    surface.seekTo(positionSec);

    let cancelled = false;
    // Polls rather than listening for canplay/onStateChange: seeking to an
    // already-buffered position often fires no event at all (the data is
    // right there), which would leave the ack waiting on something that
    // never comes.
    const check = () => {
      if (cancelled) return;
      if (Math.abs(surface.getCurrentSec() - positionSec) <= DRIFT_THRESHOLD_SEC) {
        cancelled = true;
        onReportReady(positionSec);
      }
    };
    check();
    const interval = setInterval(check, READY_POLL_MS);
    return () => { cancelled = true; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buffering, ready, positionSec]);

  // Debounced so a momentary hiccup (a single dropped frame's worth of
  // rebuffering, or the brief 'waiting' that fires right after any seek)
  // never pauses the room for everyone - only a stall that actually
  // persists is worth interrupting four other people over.
  const handleStallChange = useCallback((stalled) => {
    clearTimeout(stallTimerRef.current);
    if (!stalled) return;
    stallTimerRef.current = setTimeout(() => {
      // Re-checked at fire time, not at schedule time: the room may have
      // paused or already entered a barrier during the debounce window,
      // in which case there's nothing to report.
      if (statusRef.current === 'playing') onBuffering();
    }, STALL_DEBOUNCE_MS);
  }, [onBuffering]);

  useEffect(() => () => clearTimeout(stallTimerRef.current), []);

  // Natural end of playback. Without this, `status` stays 'playing' forever
  // past the video's actual end - expectedSec() (which extrapolates off
  // elapsed wall-clock time, see above) keeps climbing past durationSec
  // with nothing to cap it, so the drift-correction interval below
  // eventually calls surface.seekTo() on an already-ended player. Both
  // surfaces resume playback as a side effect of seeking past their end
  // (a quirk of the underlying <video>/YouTube player, not something this
  // component asks for), which is what produced the "restarts and loops
  // the first second" bug: seek-to-restart, drift grows past duration
  // again within one DRIFT_CHECK_MS tick, repeat. Reporting it as a real
  // pause (same call the ⏸ button makes) keeps `status` truthful and stops
  // expectedSec from ever running away.
  const handleEnded = useCallback(() => {
    if (statusRef.current !== 'playing' || !surfaceRef.current) return;
    onPause(surfaceRef.current.getCurrentSec());
  }, [onPause]);

  // Toasts who the room is waiting on, once per stall rather than on every
  // re-render while the barrier is up. Gated on bufferingReason==='stall'
  // (same reasoning as waitingLabel below) so a routine seek never blames
  // whoever happened to ack last.
  const toastedForRef = useRef(null);
  useEffect(() => {
    if (!buffering || bufferingReason !== 'stall') { toastedForRef.current = null; return; }
    if (!waitingOn || waitingOn.length !== 1) return;
    const [straggler] = waitingOn;
    if (toastedForRef.current === straggler.userId) return;
    toastedForRef.current = straggler.userId;
    pushToast(t('music.pausedForParticipant', { name: straggler.username }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buffering, bufferingReason, waitingOn]);

  // Purely local - never sent to the server (see onPlay/onPause/onSeek,
  // which are the only calls that talk to it). Each participant plays their
  // own copy of the video (the "watch party" model - see the plan), so
  // there's nothing to keep in sync here, unlike play/pause/seek above.
  // Re-applied on every `ready` flip too, so a freshly (re)mounted surface
  // (new video, or the very first one) picks up the stored preference
  // instead of starting at whatever that surface's own default is.
  useEffect(() => {
    if (!ready || !surfaceRef.current) return;
    surfaceRef.current.setVolume(volume);
  }, [ready, volume]);

  const handleVolumeChange = (e) => {
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    localStorage.setItem(VOLUME_STORAGE_KEY, String(vol));
  };

  // Single interval that both keeps the seek slider's displayed position
  // moving while playing, and corrects local drift against the server's
  // expected position - see DRIFT_CHECK_MS/DRIFT_THRESHOLD_SEC above.
  useEffect(() => {
    if (!ready) return undefined;
    const tick = () => {
      const surface = surfaceRef.current;
      if (!surface || dragging) return;
      const actual = surface.getCurrentSec();
      setDisplayedSec(actual);
      if (status === 'playing' && Math.abs(actual - expectedSec()) > DRIFT_THRESHOLD_SEC) {
        surface.seekTo(expectedSec());
      }
    };
    tick();
    if (status !== 'playing') return undefined;
    const interval = setInterval(tick, DRIFT_CHECK_MS);
    return () => clearInterval(interval);
  }, [ready, status, dragging, expectedSec]);

  // Phones only let media start with sound from inside a tap - a play()
  // driven by a socket event (someone else pressed play) is silently
  // refused, leaving this viewer on a frozen frame while the room plays on.
  // Detected by asking the player a moment after the room went 'playing';
  // the fix is a tap target that replays the command from a real gesture.
  // Phone-only: a desktop browser that has seen any click on the page
  // doesn't block this, and a slow first load there shouldn't flash it.
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  useEffect(() => {
    if (!isMobile || !ready || status !== 'playing') {
      setAutoplayBlocked(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      if (surfaceRef.current && !surfaceRef.current.isPlaying()) setAutoplayBlocked(true);
    }, 2500);
    return () => clearTimeout(timer);
  }, [isMobile, ready, status, updatedAt]);

  const joinPlayback = () => {
    setAutoplayBlocked(false);
    if (!surfaceRef.current) return;
    surfaceRef.current.seekTo(expectedSec());
    surfaceRef.current.play();
  };

  const handleReady = () => {
    setReady(true);
    if (surfaceRef.current) setLocalDurationSec(surfaceRef.current.getDurationSec());
  };

  // 'buffering' counts as playing for this toggle - the room IS mid-playback,
  // just briefly held at a barrier, so the button offers "pause" (which also
  // cancels the barrier server-side) rather than a second "play".
  const togglePlayPause = () => {
    const currentSec = surfaceRef.current ? surfaceRef.current.getCurrentSec() : expectedSec();
    if (status === 'playing' || buffering) onPause(currentSec); else onPlay(currentSec);
  };

  const durationSec = video.durationSec || localDurationSec || 0;

  const handleSeekChange = (e) => {
    setDragging(true);
    setDragSec(parseFloat(e.target.value));
  };
  const commitSeek = (e) => {
    const sec = parseFloat(e.target.value);
    setDragging(false);
    if (surfaceRef.current) surfaceRef.current.seekTo(sec);
    setDisplayedSec(sec);
    onSeek(sec);
  };

  const sliderValue = dragging ? dragSec : displayedSec;
  const surfaceKey = video.videoId || video.url;

  // Same tile-selection rule as VideoGridOverlay's standalone camera grid
  // (bot identities excluded, everyone else shown - camera-on as a live
  // feed, camera-off as an avatar with a speaking ring via VideoTile
  // itself) - reused here as a compact strip instead of a full grid, since
  // VoiceRoomPanel only ever renders one or the other, never both (see its
  // rendering comment).
  const cameraTiles = (participants || []).filter((p) => !p.identity.startsWith('bot:'));
  const anyCamera = cameraTiles.some((p) => p.cameraEnabled);

  // bufferingReason, not waitingOn.length, decides the wording: a plain
  // seek barrier also shrinks to a single remaining name as the others ack,
  // which would otherwise read as "we're stuck on this one person" when
  // really everyone just re-synced normally.
  const waitingNames = (waitingOn || []).map((u) => u.username).filter(Boolean);
  const waitingLabel = bufferingReason === 'stall' && waitingNames.length === 1
    ? t('music.waitingForParticipant', { name: waitingNames[0] })
    : t('music.syncingWithEveryone');

  return (
    // .VideoOverlay reuses the same fixed positioning/z-index treatment as
    // VideoGridOverlay's camera grid (see its CSS in Groups.css) -
    // .VideoWatchOverlay only adds what's specific to this single-player
    // layout (title row, surface aspect-ratio, control bar).
    // Minimized = hidden, not unmounted - see watchMinimized in
    // VoiceRoomPanel for why the player has to keep running.
    <div className={`VideoOverlay VideoWatchOverlay glass${minimized ? ' VideoOverlayMinimized' : ''}${pseudoFullscreen ? ' VideoOverlayPseudoFullscreen' : ''}${immersive ? ' VideoOverlayImmersive' : ''}${chromeHidden ? ' VideoOverlayChromeHidden' : ''}`} ref={overlayRef}>
      <div className="VideoOverlayHeader" onPointerDownCapture={immersive ? poke : undefined}>
        <span>{roomLabel}</span>
        <div className="VideoOverlayHeaderActions">
          {/* Two different things that used to share one chevron button:
              hide it just for me (local), and end it for the whole room
              (stopVideo, a real server mutation - onClose asks first). */}
          <button onClick={handleMinimize} className="VideoOverlayCloseBtn" title={t('music.minimizeWatchTogether')}><IoChevronDown /></button>
          <button onClick={handleStop} className="VideoOverlayCloseBtn" title={t('music.stopWatchTogether')}><IoClose /></button>
        </div>
      </div>
      {(video.title || video.requestedBy) && (
        <div className="VideoWatchTitleRow">
          {video.title && <div className="VideoWatchTitle">{video.title}</div>}
          {/* The session is shared by the whole room, so "who put this on"
              is otherwise unanswerable from the UI - same reasoning as the
              music bot's own requestedBy line in VoiceRoomPanel. */}
          {video.requestedBy && (
            <span className="VideoWatchRequestedBy">
              <IoPersonCircle />
              {t('music.requestedBy')}&nbsp;<b>{video.requestedBy.username}</b>
            </span>
          )}
        </div>
      )}
      <div className="VideoWatchSurfaceWrap">
        {/* Immersive only: over the player (a YouTube iframe swallows every
            touch, so it can't be what's listening) - tap to show/hide the
            controls. */}
        {immersive && <div className="VideoImmersiveTapLayer" onClick={toggleChrome} />}
        {video.sourceType === 'youtube'
          ? <YouTubeSurface key={surfaceKey} ref={surfaceRef} videoId={video.videoId} onReady={handleReady} onStallChange={handleStallChange} onEnded={handleEnded} />
          : <DirectUrlSurface key={surfaceKey} ref={surfaceRef} url={video.url} onReady={handleReady} onStallChange={handleStallChange} onEnded={handleEnded} />}
        {autoplayBlocked && !buffering && (
          <button className="VideoWatchTapToJoin" onClick={joinPlayback}>
            <IoPlayCircle />
            <span>{t('music.tapToJoin')}</span>
          </button>
        )}
        {buffering && (
          <div className="VideoWatchBufferOverlay">
            <IoSync className="VideoWatchBufferSpinner" />
            <span>{waitingLabel}</span>
          </div>
        )}
      </div>
      {anyCamera && (
        <div className="VideoWatchCameraStrip">
          {cameraTiles.map((p) => (
            <VideoTile
              key={p.identity}
              participant={p}
              track={videoTracks[p.identity]}
              occupant={occupants.find((o) => o.userId === p.identity)}
            />
          ))}
        </div>
      )}
      <div className="VideoWatchControls" onPointerDownCapture={immersive ? poke : undefined}>
        <button
          onClick={togglePlayPause}
          className="secondaryColor"
          disabled={!ready}
          title={status === 'playing' || buffering ? t('music.pause') : t('music.play')}
        >
          {status === 'playing' || buffering ? <IoPause /> : <IoPlay />}
        </button>
        <span className="VideoWatchTime">{formatTime(sliderValue)}</span>
        <input
          type="range"
          min="0"
          max={durationSec || 0}
          step="0.5"
          value={Math.min(sliderValue, durationSec || sliderValue)}
          onChange={handleSeekChange}
          onMouseUp={commitSeek}
          onTouchEnd={commitSeek}
          disabled={!ready || !durationSec}
        />
        <span className="VideoWatchTime">{formatTime(durationSec)}</span>
        {/* Local-only, unlike every other control here - see the effect
            above setVolume() is wired to. Nothing here ever reaches
            onPlay/onPause/onSeek/the server. */}
        <div className="VideoWatchVolume">
          {volume === 0 ? <IoVolumeMute /> : <IoVolumeHigh />}
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={handleVolumeChange}
          />
        </div>
        <button
          onClick={toggleFullscreen}
          className="secondaryColor"
          title={isFullscreen ? t('music.exitFullscreen') : t('music.fullscreen')}
        >
          {isFullscreen ? <IoContract /> : <IoExpand />}
        </button>
      </div>
    </div>
  );
}
